// Camera-orientation gnomon. A 128×128 rotating axis triad pinned to the
// bottom-left, mirroring the main camera. Each labelled disc (±X / ±Y /
// ±Z) is clickable for a viewpoint snap. Reset stays bound to the R key
// + palette command (no dedicated button — kept the gnomon distraction-
// free).
//
// Implementation note: the gnomon lives in its own tiny WebGLRenderer + DOM
// canvas rather than rendering into a sub-viewport of the main canvas.
// That keeps ViewHelper's built-in hit-test math correct (it expects the
// full domElement to be the gizmo extent) and avoids monkey-patching its
// closure-private viewport rect. The cost is one extra GL context.

import * as THREE from 'three';
import { ViewHelper } from 'three/examples/jsm/helpers/ViewHelper.js';
import { store } from '@store/instance';
import { INTENTS } from '@store/intents';
import type { ViewerContext } from '@app/context';

// Keep the canvas at 128: three's ViewHelper hardcodes a 128px render
// viewport (positioned from offsetWidth − 128), so a smaller canvas
// mis-centers the triad. Visual shrink is done via a CSS transform instead.
const DIM = 128;

// Collapsed/expanded state persists across reloads; below this viewport width
// (phones) the gnomon starts collapsed so it doesn't hog the bottom-left corner.
const COLLAPSE_LS_KEY = 'magviz:gnomonCollapsed:v1';
const PHONE_MAX_WIDTH = 640;

function loadCollapsed(): boolean {
    try {
        const raw = localStorage.getItem(COLLAPSE_LS_KEY);
        if (raw === 'true') return true;
        if (raw === 'false') return false;
    } catch { /* private mode / quota — fall through to the default */ }
    try { return window.matchMedia(`(max-width: ${PHONE_MAX_WIDTH}px)`).matches; }
    catch { return false; }
}

function saveCollapsed(collapsed: boolean): void {
    try { localStorage.setItem(COLLAPSE_LS_KEY, collapsed ? 'true' : 'false'); }
    catch { /* ignore */ }
}

// Sprite tips lerp between these scale factors based on view-space depth so
// the tip closest to the camera reads bigger, the back tips read smaller.
// Range is 2× — large enough to feel parallax-like, small enough that the
// closest tip stays a comfortable hit target.
const NEAR_SCALE = 1.30;
const FAR_SCALE = 0.65;

// Opacity for the negative-direction tips. ViewHelper r169 draws them
// black at 0.2; we tint them with each axis hue at this reduced opacity
// so the colour stays legible without competing with the positive tips.
const NEG_TIP_OPACITY = 0.32;

// Black-and-white mode: all three axes share one monochrome hue (white discs
// + stems). The X/Y/Z labels on the positive tips disambiguate them, so colour
// isn't needed. Theme axis colours are intentionally ignored here.
const BW_AXIS_HEX = '#f2f2f2';
const DEFAULT_AXIS_HEX = { X: BW_AXIS_HEX, Y: BW_AXIS_HEX, Z: BW_AXIS_HEX } as const;
type AxisKey = keyof typeof DEFAULT_AXIS_HEX;

// ViewHelper labels its discs by three.js world axes, but magviz draws the
// physical frame rotated into three space (physical Y → three X, Z → three Y,
// X → three Z; see @physics/coords). The disc geometry and click-to-snap stay
// in three space — only the printed letter is remapped to the physical axis it
// actually points along, so the gnomon reads in the same frame as the scene.
const PHYSICAL_LABEL: Record<AxisKey, string> = { X: 'Y', Y: 'Z', Z: 'X' };

// Snap-to-axis animation. We drive position + orientation ourselves with
// an eased progress instead of delegating to ViewHelper.update(), which
// clamps the last frame to "whatever residual is left" and produces a
// visible deceleration-then-stop — especially on multi-axis paths to
// ±Y where the orientation's angular distance differs from the position
// arc's. With a single eased progress parameter, both finish together
// and the last frame's motion is naturally tiny (eased), not clamped.
const SNAP_DURATION_SEC = 0.5;
const PRESET_DIRS: Readonly<Record<string, THREE.Vector3>> = {
    posX: new THREE.Vector3(1, 0, 0),
    negX: new THREE.Vector3(-1, 0, 0),
    posY: new THREE.Vector3(0, 1, 0),
    negY: new THREE.Vector3(0, -1, 0),
    posZ: new THREE.Vector3(0, 0, 1),
    negZ: new THREE.Vector3(0, 0, -1),
};

function easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function gnomonHexFor(_viewer: ViewerContext): Record<AxisKey, string> {
    // Monochrome regardless of theme — see BW_AXIS_HEX.
    return { X: DEFAULT_AXIS_HEX.X, Y: DEFAULT_AXIS_HEX.Y, Z: DEFAULT_AXIS_HEX.Z };
}

function makeDotTexture(hex: string, label?: string): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (ctx) {
        ctx.beginPath();
        ctx.arc(32, 32, 14, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.fillStyle = hex;
        ctx.fill();
        if (label) {
            ctx.font = '24px Arial';
            ctx.textAlign = 'center';
            ctx.fillStyle = '#000000';
            ctx.fillText(label, 32, 41);
        }
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

/** Identify each axis cylinder by the rotation ViewHelper applies during
 *  construction. r169 rotates Y by +π/2 about Z, Z by −π/2 about Y, and
 *  leaves X unrotated. Robust enough until upstream changes the helper. */
function axisOfCylinder(mesh: THREE.Mesh): AxisKey {
    if (Math.abs(mesh.rotation.z - Math.PI / 2) < 1e-3) return 'Y';
    if (Math.abs(mesh.rotation.y + Math.PI / 2) < 1e-3) return 'Z';
    return 'X';
}

export function installCameraGnomon(viewer: ViewerContext): () => void {
    const camera = viewer.three.camera;
    const controls = viewer.three.controls;
    if (!camera || !controls) return () => { /* three stack not ready */ };

    const wrap = document.createElement('div');
    wrap.className = 'camera-gnomon';
    wrap.id = 'camera-gnomon';
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', 'Camera orientation gnomon');

    const canvas = document.createElement('canvas');
    canvas.width = DIM;
    canvas.height = DIM;
    canvas.className = 'camera-gnomon-canvas';
    canvas.title = 'Click an axis to snap to that view';
    wrap.appendChild(canvas);

    document.body.appendChild(wrap);

    // Persistent compass toggle in the bottom control cluster (flex row): an
    // on/off control that shows or hides the corner gimbal. `aria-pressed`
    // reflects "gimbal shown". The canvas `wrap` stays a separate body-level
    // element in the lower-left corner.
    const toggleBtn = document.createElement('button');
    toggleBtn.type = 'button';
    toggleBtn.id = 'camera-gnomon-toggle';
    toggleBtn.className = 'camera-gnomon-toggle';
    toggleBtn.title = 'Toggle orientation gnomon';
    toggleBtn.setAttribute('aria-label', 'Toggle orientation gnomon');
    toggleBtn.setAttribute('aria-pressed', 'false');
    const toggleIcon = document.createElement('i');
    toggleIcon.className = 'fas fa-compass';
    toggleIcon.setAttribute('aria-hidden', 'true');
    toggleBtn.appendChild(toggleIcon);
    (document.getElementById('bottom-cluster') ?? document.body).appendChild(toggleBtn);

    let collapsed = loadCollapsed();
    const applyCollapsed = (next: boolean): void => {
        collapsed = next;
        document.body.classList.toggle('gnomon-collapsed', next);
        toggleBtn.setAttribute('aria-pressed', String(!next)); // pressed = gimbal shown
        // Don't resume a half-finished snap when re-expanding.
        if (next) animActive = false;
        saveCollapsed(next);
    };

    const onToggle = (e: MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        applyCollapsed(!collapsed);
    };
    toggleBtn.addEventListener('click', onToggle);

    // Dedicated mini-renderer with an alpha buffer so the page background
    // shows through. devicePixelRatio honoured for crisp labels on retina.
    const gnomonRenderer = new THREE.WebGLRenderer({
        canvas, alpha: true, antialias: true,
    });
    gnomonRenderer.setPixelRatio(window.devicePixelRatio);
    gnomonRenderer.setSize(DIM, DIM, false);

    const viewHelper = new ViewHelper(camera, canvas);
    viewer.three.viewHelper = viewHelper;

    // Walk the helper's children once: index the 6 tip sprites by ±axis,
    // and the 3 axis cylinders by axis. We'll re-skin all 9 from the
    // active theme below, and re-skin again on every theme change.
    const tips: THREE.Sprite[] = [];
    const cylinderByAxis = new Map<AxisKey, THREE.Mesh>();
    for (const child of viewHelper.children) {
        const sprite = child as THREE.Sprite;
        if (sprite.isSprite) {
            tips.push(sprite);
            continue;
        }
        const mesh = child as THREE.Mesh;
        if (mesh.isMesh) cylinderByAxis.set(axisOfCylinder(mesh), mesh);
    }

    // Faint connector stems for the negative directions. ViewHelper only draws
    // the +X/+Y/+Z stems, leaving the −tips floating; these thin, low-opacity
    // cylinders run center → −tip so each axis reads as one continuous line,
    // the negative half far fainter than the prominent positive half. Added
    // after the child-walk above so they don't land in cylinderByAxis. One
    // shared geometry (along −X, spanning [−0.82, 0]) + one shared material;
    // per-mesh rotation aims it down −Y / −Z (same rotations ViewHelper uses
    // for its +Y / +Z stems).
    const negStemGeom = new THREE.CylinderGeometry(0.013, 0.013, 0.82, 6)
        .rotateZ(Math.PI / 2)
        .translate(-0.41, 0, 0);
    const negStemMat = new THREE.MeshBasicMaterial({
        color: BW_AXIS_HEX,
        toneMapped: false,
        transparent: true,
        opacity: 0.16,
    });
    const negStemX = new THREE.Mesh(negStemGeom, negStemMat);
    const negStemY = new THREE.Mesh(negStemGeom, negStemMat);
    negStemY.rotation.z = Math.PI / 2;
    const negStemZ = new THREE.Mesh(negStemGeom, negStemMat);
    negStemZ.rotation.y = -Math.PI / 2;
    viewHelper.add(negStemX, negStemY, negStemZ);

    /** Re-skin cylinders + tip sprites from the active theme. Called on
     *  install and on every UI_THEME_APPLY. Old materials/textures are
     *  disposed before being overwritten; viewHelper.dispose() picks up
     *  the latest swapped-in handles on teardown — no double-free. */
    const applyThemeColors = (): void => {
        const colors = gnomonHexFor(viewer);

        for (const [axis, mesh] of cylinderByAxis) {
            const material = mesh.material as THREE.MeshBasicMaterial;
            material.color.set(colors[axis]);
        }

        for (const sprite of tips) {
            const type = (sprite.userData as { type?: string }).type ?? '';
            if (!type) continue;
            const isPos = type.startsWith('pos');
            const axis = type.slice(3) as AxisKey;
            const hex = colors[axis];
            if (!hex) continue;
            const oldMat = sprite.material;
            const oldMap = oldMat.map;
            const newMap = makeDotTexture(hex, isPos ? PHYSICAL_LABEL[axis] : undefined);
            sprite.material = new THREE.SpriteMaterial({
                map: newMap,
                toneMapped: false,
                transparent: true,
                opacity: isPos ? 1.0 : NEG_TIP_OPACITY,
            });
            if (oldMap) oldMap.dispose();
            oldMat.dispose();
        }
    };

    applyThemeColors();
    const offTheme = store.onIntent(INTENTS.UI_THEME_APPLY, () => {
        applyThemeColors();
    });

    // Per-frame depth-scale: rotate each tip's base position by the same
    // quaternion ViewHelper.render() will use, then lerp tip.scale based on
    // the rotated z (range [-1, 1] for the 6 unit-vector tips).
    const helperQuat = new THREE.Quaternion();
    const tipPos = new THREE.Vector3();
    const updateTipScales = (): void => {
        helperQuat.copy(camera.quaternion).invert();
        for (const tip of tips) {
            tipPos.copy(tip.position).applyQuaternion(helperQuat);
            const t = (tipPos.z + 1) * 0.5;
            const s = FAR_SCALE + (NEAR_SCALE - FAR_SCALE) * t;
            tip.scale.set(s, s, 1);
        }
    };

    // Pick the clicked tip ourselves. ViewHelper.handleClick would do
    // the raycast for us, but it also seeds rotateTowards-based state
    // we don't want to use (see the animation-driver block below).
    // Replicate its math against our own `tips` references so we can
    // hand the preset key to our own slerp instead.
    // Tighter than ViewHelper.render()'s fixed ±2 frustum so the visible triad
    // fills the panel instead of ~2/3 of it. Used for BOTH the render framing
    // and click-picking, so the two stay consistent (a render/pick frustum
    // mismatch would misalign the hit targets).
    const GNOMON_HALF = 1.5;
    const raycaster = new THREE.Raycaster();
    const mouseNdc = new THREE.Vector2();
    const orthoCam = new THREE.OrthographicCamera(
        -GNOMON_HALF, GNOMON_HALF, GNOMON_HALF, -GNOMON_HALF, 0, 4,
    );
    orthoCam.position.set(0, 0, 2);
    orthoCam.updateMatrixWorld();

    const pickTipPreset = (e: PointerEvent): string | null => {
        const rect = canvas.getBoundingClientRect();
        mouseNdc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        mouseNdc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(mouseNdc, orthoCam);
        // Mirror the inverse-camera rotation ViewHelper.render() applies
        // so the raycast sees the tips in their currently-rendered pose.
        viewHelper.quaternion.copy(camera.quaternion).invert();
        viewHelper.updateMatrixWorld();
        const hits = raycaster.intersectObjects(tips);
        const hit = hits[0];
        if (!hit) return null;
        return (hit.object.userData as { type?: string }).type ?? null;
    };

    // Custom snap animation. We drive camera.position along the
    // great-circle arc ourselves with a fixed-duration eased progress;
    // camera.quaternion is allowed to follow naturally via the main
    // loop's controls.update() → lookAt(target) each frame. Benefits
    // over ViewHelper.update():
    //
    //   • One progress parameter (`t`) governs the whole animation,
    //     so position and orientation can't desynchronise the way q1
    //     and camera.quaternion do under ViewHelper's twin rotateTowards
    //     calls (which use the same step rate but cover different
    //     angular distances on diagonal paths).
    //   • easeInOutCubic ramps velocity to zero at t=1, so the final
    //     frame's motion is naturally tiny — no abrupt clamp-to-target
    //     stop, which is what reads as a "skip" on ±Y diagonal snaps.
    //   • Main scene and gnomon stay visually consistent: both use
    //     camera.quaternion = lookAt(target) (whatever the main loop's
    //     controls.update() produces), so they never disagree about the
    //     camera's pose during the animation.
    const animFocal = new THREE.Vector3();
    const animStartDirQuat = new THREE.Quaternion();
    const animEndDirQuat = new THREE.Quaternion();
    const dummyForLookAt = new THREE.Object3D();
    const tmpVec = new THREE.Vector3();
    const tmpQuat = new THREE.Quaternion();
    let animActive = false;
    let animElapsed = 0;
    let animRadius = 0;

    const startSnap = (presetKey: string): void => {
        const targetDir = PRESET_DIRS[presetKey];
        if (!targetDir) return;
        animFocal.copy(controls.target);
        animRadius = camera.position.distanceTo(animFocal);
        // Direction quaternions: dummy at the focal point, "looking at" the
        // camera (start) vs the target position. Slerping between these and
        // applying to (0,0,1) traces the great-circle arc on the
        // radius-sphere around the focal point — the same arc ViewHelper
        // would draw, just with our own progress curve.
        dummyForLookAt.position.copy(animFocal);
        dummyForLookAt.lookAt(camera.position);
        animStartDirQuat.copy(dummyForLookAt.quaternion);
        tmpVec.copy(targetDir).multiplyScalar(animRadius).add(animFocal);
        dummyForLookAt.lookAt(tmpVec);
        animEndDirQuat.copy(dummyForLookAt.quaternion);
        animElapsed = 0;
        animActive = true;
    };

    viewer.three.viewHelperTick = (dt: number): void => {
        if (collapsed) return; // hidden — skip the GL paint + tip-scale work
        viewHelper.center.copy(controls.target);
        if (animActive) {
            animElapsed += dt;
            const t = Math.min(1, animElapsed / SNAP_DURATION_SEC);
            const eased = easeInOutCubic(t);
            tmpQuat.copy(animStartDirQuat).slerp(animEndDirQuat, eased);
            camera.position
                .set(0, 0, 1)
                .applyQuaternion(tmpQuat)
                .multiplyScalar(animRadius)
                .add(animFocal);
            if (t >= 1) animActive = false;
        }
        updateTipScales();
        // Render ourselves rather than viewHelper.render(): the latter's ortho
        // frustum is hardcoded at ±2 (the ~2/3 fill). We mirror its inverse-
        // camera pose and draw through our tighter orthoCam instead.
        viewHelper.quaternion.copy(camera.quaternion).invert();
        viewHelper.updateMatrixWorld();
        gnomonRenderer.render(viewHelper, orthoCam);
    };

    const onPointerDown = (e: PointerEvent): void => {
        if (animActive) return;
        const preset = pickTipPreset(e);
        if (preset) startSnap(preset);
    };
    canvas.addEventListener('pointerdown', onPointerDown);

    // Settle the initial collapsed/expanded class now that animActive exists.
    applyCollapsed(collapsed);

    return () => {
        canvas.removeEventListener('pointerdown', onPointerDown);
        toggleBtn.removeEventListener('click', onToggle);
        offTheme();
        viewHelper.remove(negStemX, negStemY, negStemZ);
        negStemGeom.dispose();
        negStemMat.dispose();
        viewHelper.dispose();
        gnomonRenderer.dispose();
        viewer.three.viewHelper = null;
        viewer.three.viewHelperTick = null;
        if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
        if (toggleBtn.parentNode) toggleBtn.parentNode.removeChild(toggleBtn);
        // Clear the shared body class so a reinstall (projection swap) re-derives
        // it from persisted state instead of inheriting a stale one.
        document.body.classList.remove('gnomon-collapsed');
    };
}
